import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LoadingState } from "@/components/interactions/LoadingState";
import { cn } from "@/lib/utils";
import { getCurrentUserId } from "@/services";
import {
  Users,
  Calendar,
  AlertTriangle,
  TrendingUp,
  ChevronRight,
  MapPin,
  Clock,
} from "lucide-react";
import {
  useAssignedFarmers,
  useVisitSchedule,
  type AssignedFarmer,
} from "@/hooks/useSupervisorData";

function daysSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

function visitTrafficLight(lastVisitDate: string | null): "green" | "yellow" | "red" {
  const days = daysSince(lastVisitDate);
  if (days === null) return "red";
  if (days <= 7) return "green";
  if (days <= 14) return "yellow";
  return "red";
}

const trafficColors = {
  green: "bg-green-500",
  yellow: "bg-yellow-500",
  red: "bg-red-500",
};

export const SupervisorDashboardScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = getCurrentUserId();

  const { data: farmers = [], isLoading: farmersLoading } = useAssignedFarmers(userId);
  const { data: upcomingVisits = [], isLoading: visitsLoading } = useVisitSchedule(userId);

  const isLoading = farmersLoading || visitsLoading;

  // KPI calculations
  const totalFarmers = farmers.length;
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0];
  const visitsThisMonth = upcomingVisits.filter(
    (v) => v.scheduled_date >= monthStart && v.status === "completed"
  ).length;
  const overdueVisits = upcomingVisits.filter((v) => v.status === "overdue").length;
  const avgScore =
    farmers.length > 0
      ? Math.round(
          farmers.reduce((s, f) => s + (f.latest_score || 0), 0) /
            farmers.filter((f) => f.latest_score).length || 1
        )
      : 0;

  if (isLoading) {
    return (
      <Screen topBarProps={{ title: t("supervisor.dashboard", "Supervisor Dashboard"), showBack: true }}>
        <LoadingState message={t("common.loading", "Loading...")} />
      </Screen>
    );
  }

  return (
    <Screen topBarProps={{ title: t("supervisor.dashboard", "Supervisor Dashboard"), showBack: true }}>
      <div className="p-4 space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3">
          {[
            {
              label: t("supervisor.assignedFarmers", "Assigned Farmers"),
              value: totalFarmers,
              icon: Users,
              color: "text-blue-600",
              bg: "bg-blue-50 dark:bg-blue-950",
            },
            {
              label: t("supervisor.visitsThisMonth", "Visits This Month"),
              value: visitsThisMonth,
              icon: Calendar,
              color: "text-green-600",
              bg: "bg-green-50 dark:bg-green-950",
            },
            {
              label: t("supervisor.overdueVisits", "Overdue Visits"),
              value: overdueVisits,
              icon: AlertTriangle,
              color: overdueVisits > 0 ? "text-red-600" : "text-muted-foreground",
              bg: overdueVisits > 0 ? "bg-red-50 dark:bg-red-950" : "bg-muted",
            },
            {
              label: t("supervisor.avgScore", "Avg Farm Score"),
              value: avgScore || "—",
              icon: TrendingUp,
              color: "text-purple-600",
              bg: "bg-purple-50 dark:bg-purple-950",
            },
          ].map((kpi, idx) => {
            const KpiIcon = kpi.icon;
            return (
              <motion.div
                key={kpi.label}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className={cn("p-3 space-y-1", kpi.bg)}>
                  <div className="flex items-center gap-2">
                    <KpiIcon className={cn("w-4 h-4", kpi.color)} />
                    <span className="text-xs text-muted-foreground">{kpi.label}</span>
                  </div>
                  <p className={cn("text-2xl font-bold", kpi.color)}>{kpi.value}</p>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Upcoming Visits */}
        {upcomingVisits.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {t("supervisor.upcomingVisits", "Upcoming Visits")}
            </h3>
            {upcomingVisits.slice(0, 5).map((visit, idx) => (
              <motion.div
                key={visit.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card
                  className="p-3 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => navigate(`/supervisor/history/${visit.farmer_id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {visit.farmer?.full_name || t("common.anonymous", "Anonymous")}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{visit.scheduled_date}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {t(`supervisor.${visit.visit_type}`, visit.visit_type)}
                      </Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Farmer List */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            {t("supervisor.assignedFarmers", "Assigned Farmers")}
          </h3>
          {farmers.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              {t("supervisor.noFarmers", "No farmers assigned")}
            </Card>
          ) : (
            farmers.map((farmer, idx) => (
              <FarmerCard
                key={farmer.id}
                farmer={farmer}
                index={idx}
                onClick={() => navigate(`/supervisor/history/${farmer.id}`)}
                t={t}
              />
            ))
          )}
        </div>
      </div>
    </Screen>
  );
};

const FarmerCard: React.FC<{
  farmer: AssignedFarmer;
  index: number;
  onClick: () => void;
  t: ReturnType<typeof import("react-i18next").useTranslation>["t"];
}> = ({ farmer, index, onClick, t }) => {
  const light = visitTrafficLight(farmer.last_visit_date);
  const days = daysSince(farmer.last_visit_date);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Card
        className="p-3 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
        onClick={onClick}
      >
        {/* Traffic light */}
        <div className={cn("w-3 h-3 rounded-full shrink-0", trafficColors[light])} />

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">
            {farmer.full_name || t("common.anonymous", "Anonymous")}
          </p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {farmer.region && (
              <>
                <MapPin className="w-3 h-3" />
                <span className="truncate">{farmer.region}</span>
              </>
            )}
            {farmer.active_batch_status && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {farmer.active_batch_status}
              </Badge>
            )}
          </div>
        </div>

        {/* Score */}
        {farmer.latest_score != null && (
          <div className="text-right shrink-0">
            <p className="text-lg font-bold text-foreground">{farmer.latest_score}</p>
            <p className="text-[10px] text-muted-foreground">score</p>
          </div>
        )}

        {/* Last visit */}
        <div className="text-right shrink-0 text-xs text-muted-foreground">
          {days !== null ? (
            <span>{days === 0 ? "Today" : `${days}d ago`}</span>
          ) : (
            <span className="text-red-500">{t("supervisor.noVisit", "No visit")}</span>
          )}
        </div>

        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </Card>
    </motion.div>
  );
};
